import { format, isValid, parseISO } from "date-fns";
import { enUS } from "date-fns/locale";
import i18next from "i18next";
import { sameDateInUTC } from "@features/cashbackWizard2.0/utils/helpers";
import type { Locale } from "date-fns";

export const formatDateHourly = (value?: string | number) => {
  if (!value) return "";
  return format(new Date(value), "HH:mm");
};

export const formatDateYYYY = (value?: string | number) => {
  if (!value) return "";
  return format(new Date(value), "yyyy");
};

export const formatDateOrString = (dateOrString?: Date | string | null) => {
  if (!dateOrString) return "";
  const date =
    dateOrString instanceof Date
      ? dateOrString
      : sameDateInUTC(parseISO(dateOrString));
  return isValid(date) ? format(date, "dd.MM.yyyy") : "";
};

export const convertDateToOnlyDateString = (
  value: Date | string | null | undefined
): string | undefined => {
  if (!value) return;
  const date = typeof value === "string" ? new Date(value) : value;
  if (!(date instanceof Date)) return date;

  return format(date, "yyyy-MM-dd");
};

export const formatDateMMMYY = (
  value?: string | number,
  locale: Locale = enUS
) => {
  if (!value) return "";
  return `${format(new Date(value), "MMM", {
    locale,
  }).replace(/\.$/, "")} '${format(new Date(value), "yy")}`;
};

export const formatDateDDMMYY = (value?: string | number) => {
  if (!value) return "";
  return `${format(new Date(value), "dd.MM")}.${format(new Date(value), "yy")}`;
};

export const formatDateDDMMYYYY = (value?: Date | string | number) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return format(new Date(date), "dd.MM.yyyy");
};

export const formatDateDDMM = (value?: string | number) => {
  if (!value) return "";
  const date = new Date(value);
  return `${format(date, "MM")}'${format(date, "yy")}`;
};

export const formatDateDDMMM = (
  value?: string | number,
  locale: Locale = enUS
) => {
  if (!value) return "";
  return `${format(new Date(value), "dd")}. ${format(new Date(value), "MMM", {
    locale,
  }).replace(/\.$/, "")}`;
};

export const formatDateYYYYmmDD = (value?: Date | string | number) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return format(date, "yyyy-MM-dd");
};

export const getDateFormatter = (
  intervalType: IntervalType,
  value: string | number,
  locale: Locale = enUS
) => {
  const dayTypes = ["oneWeek", "oneMonth"];
  if (dayTypes.includes(intervalType)) return formatDateDDMMM(value, locale);
  return formatDateMMMYY(value, locale);
};

export const numberOfDaysToText = (days: number) => {
  if (days < 1) return "";
  if (days === 1) return i18next.t("expiresIn.day");
  if (days <= 30) return i18next.t("expiresIn.days", { days });
  const months = Math.ceil(days / 30);
  if (months === 1) return i18next.t("expiresIn.month");
  return i18next.t("expiresIn.months", { months });
};
